import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Divider,
  Paper,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  AccountCircle as AccountIcon,
  Group as GroupIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";

const ViewDetailsModal = ({
  open,
  onClose,
  viewingItem,
  viewType,
  canEditSettings,
  onEdit,
}) => {
  const handleEdit = () => {
    onClose();
    onEdit(viewingItem, viewType);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(75,90,182,0.18)",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          color: "#4B5AB6",
          textAlign: "center",
          pb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        {viewType === "account" ? <AccountIcon /> : <GroupIcon />}
        {viewType === "account" ? "Account Details" : "Group Details"}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {!viewingItem ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading details...
            </Typography>
          </Box>
        ) : viewType === "account" ? (
          <AccountDetailsView
            viewingItem={viewingItem}
            canEditSettings={canEditSettings}
          />
        ) : (
          <GroupDetailsView
            viewingItem={viewingItem}
            canEditSettings={canEditSettings}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: "space-between" }}>
        <Button onClick={onClose} sx={{ color: "#64748b" }}>
          Close
        </Button>
        {canEditSettings && viewingItem && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{
              bgcolor: "#4B5AB6",
              "&:hover": { bgcolor: "#3a4891" },
            }}
          >
            Edit {viewType === "account" ? "Account" : "Group"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Account Details Component
const AccountDetailsView = ({ viewingItem, canEditSettings }) => {
  return (
    <Grid container spacing={3}>
      {/* Basic Information */}
      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "#1e293b", mb: 2 }}
        >
          Basic Information
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#64748b" }}
            >
              Account ID
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontFamily: "monospace", color: "#1e293b" }}
            >
              {viewingItem?.accountId || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#64748b" }}
            >
              Account Name
            </Typography>
            <Typography variant="body1" sx={{ color: "#1e293b" }}>
              {viewingItem?.accountName || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#64748b" }}
            >
              Type
            </Typography>
            <Chip
              label={viewingItem?.type || "Unknown"}
              size="small"
              sx={{
                bgcolor:
                  viewingItem?.type === "Social Media"
                    ? "#e0f2fe"
                    : viewingItem?.type === "Email"
                    ? "#f3e5f5"
                    : "#e8f5e8",
                color:
                  viewingItem?.type === "Social Media"
                    ? "#0277bd"
                    : viewingItem?.type === "Email"
                    ? "#7b1fa2"
                    : "#2e7d32",
                fontWeight: 500,
              }}
            />
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#64748b" }}
            >
              Provider
            </Typography>
            <Typography variant="body1" sx={{ color: "#1e293b" }}>
              {viewingItem?.provider || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#64748b" }}
            >
              Status
            </Typography>
            <Chip
              label={viewingItem?.status || "Unknown"}
              size="small"
              color={viewingItem?.status === "Active" ? "success" : "default"}
            />
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#64748b" }}
            >
              Connected By
            </Typography>
            <Typography variant="body1" sx={{ color: "#1e293b" }}>
              {viewingItem?.connectedBy || "N/A"}
            </Typography>
          </Box>
          {viewingItem?.appLink && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#64748b" }}
              >
                App/Media Link
              </Typography>
              <Typography variant="body1" sx={{ color: "#1e293b" }}>
                <a
                  href={viewingItem.appLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#4B5AB6",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    wordBreak: "break-all",
                  }}
                >
                  🔗 {viewingItem.appLink}
                  <OpenInNewIcon sx={{ fontSize: 16, ml: 0.5 }} />
                </a>
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>

      {/* Account Configuration */}
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "#1e293b", mb: 2 }}
        >
          Account Configuration
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#64748b" }}
            >
              Identifier
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#1e293b",
                fontFamily: "monospace",
                wordBreak: "break-all",
              }}
            >
              {viewingItem?.identifier || "N/A"}
            </Typography>
          </Box>
          {viewingItem?.group && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#64748b" }}
              >
                Group
              </Typography>
              <Chip
                label={viewingItem.group}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            </Box>
          )}
          {/* UPDATED: Show expiry info in basic configuration */}
          {(viewingItem?.accessTokenExpiry || viewingItem?.expiry) && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#64748b" }}
              >
                Access Token Expiry
              </Typography>
              <Typography variant="body1" sx={{ color: "#1e293b" }}>
                {viewingItem.accessTokenExpiry || viewingItem.expiry}
              </Typography>
            </Box>
          )}
          {viewingItem?.pageTokenExpiry && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#64748b" }}
              >
                Page Token Expiry
              </Typography>
              <Typography variant="body1" sx={{ color: "#1e293b" }}>
                {viewingItem.pageTokenExpiry}
              </Typography>
            </Box>
          )}
          {viewingItem?.regionOrFromEmail && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#64748b" }}
              >
                {viewingItem?.type === "Email" ? "From Email" : "Region"}
              </Typography>
              <Typography variant="body1" sx={{ color: "#1e293b" }}>
                {viewingItem.regionOrFromEmail}
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>

      {/* Notes Section - Always show with proper text wrapping */}
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "#1e293b", mb: 2 }}
        >
          📝 Notes
        </Typography>
        <Paper
          sx={{
            p: 3,
            bgcolor: "#f8fafc",
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            // ADDED: Proper overflow handling
            overflow: "hidden",
            wordWrap: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {viewingItem?.notes && viewingItem.notes.trim() !== "" ? (
            <Typography
              variant="body1"
              sx={{
                color: "#475569",
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                // ADDED: Comprehensive text wrapping and overflow handling
                wordBreak: "break-word",
                overflowWrap: "anywhere", // Allows breaking at any character if needed
                hyphens: "auto", // Adds hyphens for better readability
                maxWidth: "100%",
                overflow: "hidden",
                // ADDED: Better handling for very long strings like tokens
                fontFamily:
                  viewingItem.notes.length > 200 ? "monospace" : "inherit",
                fontSize: viewingItem.notes.length > 500 ? "0.825rem" : "1rem",
                // ADDED: Scroll for extremely long content
                maxHeight: "300px",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f5f9",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#cbd5e1",
                  borderRadius: "3px",
                  "&:hover": {
                    backgroundColor: "#94a3b8",
                  },
                },
              }}
            >
              {viewingItem.notes}
            </Typography>
          ) : (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: "#94a3b8", fontStyle: "italic", mb: 1 }}
              >
                No notes available for this account.
              </Typography>
              <Typography variant="caption" sx={{ color: "#cbd5e1" }}>
                You can add notes when editing this account.
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>

      {/* Technical Details */}
      {canEditSettings && (
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "#1e293b", mb: 2 }}
          >
            Technical Details
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2 }}>
            {/* Social Media specific fields */}
            {viewingItem?.type === "Social Media" && (
              <>
                {viewingItem?.username && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "#64748b" }}
                    >
                      Username/Page ID
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#1e293b",
                        fontFamily: "monospace",
                        // ADDED: Text wrapping for technical fields
                        wordBreak: "break-all",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {viewingItem.username}
                    </Typography>
                  </Box>
                )}
                {viewingItem?.accessToken && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "#64748b" }}
                    >
                      Access Token
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#1e293b",
                        fontFamily: "monospace",
                        // UPDATED: Better token display with proper wrapping
                        wordBreak: "break-all",
                        overflowWrap: "anywhere",
                        bgcolor: "#f8fafc",
                        p: 2,
                        borderRadius: 1,
                        border: "1px solid #e2e8f0",
                        fontSize: "0.825rem",
                        lineHeight: 1.5,
                        maxHeight: "200px",
                        overflowY: "auto",
                        // ADDED: Custom scrollbar for token display
                        "&::-webkit-scrollbar": {
                          width: "4px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#f1f5f9",
                          borderRadius: "2px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#cbd5e1",
                          borderRadius: "2px",
                          "&:hover": {
                            backgroundColor: "#94a3b8",
                          },
                        },
                      }}
                    >
                      {viewingItem.accessToken}
                    </Typography>
                  </Box>
                )}
                {(viewingItem?.accessTokenExpiry || viewingItem?.expiry) && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "#64748b" }}
                    >
                      🗓️ Access Token Expiry Date
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#1e293b" }}>
                      {viewingItem.accessTokenExpiry || viewingItem.expiry}
                    </Typography>
                  </Box>
                )}
                {viewingItem?.pageToken && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "#64748b" }}
                    >
                      🔑 Page Token
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#1e293b",
                        fontFamily: "monospace",
                        // UPDATED: Better page token display with proper wrapping
                        wordBreak: "break-all",
                        overflowWrap: "anywhere",
                        bgcolor: "#f0f9ff",
                        p: 2,
                        borderRadius: 1,
                        border: "1px solid #e0f2fe",
                        fontSize: "0.825rem",
                        lineHeight: 1.5,
                        maxHeight: "200px",
                        overflowY: "auto",
                        // ADDED: Custom scrollbar for page token display
                        "&::-webkit-scrollbar": {
                          width: "4px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#f1f5f9",
                          borderRadius: "2px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#0ea5e9",
                          borderRadius: "2px",
                          "&:hover": {
                            backgroundColor: "#0284c7",
                          },
                        },
                      }}
                    >
                      {viewingItem.pageToken}
                    </Typography>
                  </Box>
                )}
                {viewingItem?.pageTokenExpiry && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "#64748b" }}
                    >
                      📅 Page Token Expiry Date
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#1e293b" }}>
                      {viewingItem.pageTokenExpiry}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Email specific fields with proper wrapping */}
            {viewingItem?.type === "Email" && (
              <>
                {viewingItem?.smtpHost && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: "#64748b" }}
                      >
                        SMTP Host
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "#1e293b",
                          fontFamily: "monospace",
                          wordBreak: "break-all",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {viewingItem.smtpHost}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: "#64748b" }}
                      >
                        SMTP Port
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: "#1e293b", fontFamily: "monospace" }}
                      >
                        {viewingItem.smtpPort}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {viewingItem?.fromName && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "#64748b" }}
                    >
                      From Name
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#1e293b",
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {viewingItem.fromName}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* SMS specific fields with proper wrapping */}
            {viewingItem?.type === "SMS" && (
              <>
                {viewingItem?.senderId && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "#64748b" }}
                    >
                      Sender ID
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#1e293b",
                        fontFamily: "monospace",
                        wordBreak: "break-all",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {viewingItem.senderId}
                    </Typography>
                  </Box>
                )}
                {viewingItem?.country && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "#64748b" }}
                    >
                      Country
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#1e293b",
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {viewingItem.country}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Grid>
      )}

      {/* Timeline */}
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "#1e293b", mb: 2 }}
        >
          Timeline
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          {viewingItem?.createdAt && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#64748b" }}
              >
                Created At
              </Typography>
              <Typography variant="body1" sx={{ color: "#1e293b" }}>
                {new Date(viewingItem.createdAt).toLocaleString()}
              </Typography>
            </Box>
          )}
          {viewingItem?.updatedAt && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#64748b" }}
              >
                Last Updated
              </Typography>
              <Typography variant="body1" sx={{ color: "#1e293b" }}>
                {new Date(viewingItem.updatedAt).toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

// Group Details Component
const GroupDetailsView = ({ viewingItem, canEditSettings }) => {
  return (
    <Grid container spacing={3}>
      {/* Basic Information */}
      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "#1e293b", mb: 2 }}
        >
          Basic Information
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#64748b" }}
            >
              Group ID
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontFamily: "monospace", color: "#1e293b" }}
            >
              {viewingItem?.groupId || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#64748b" }}
            >
              Group Name
            </Typography>
            <Typography variant="body1" sx={{ color: "#1e293b" }}>
              {viewingItem?.groupName || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#64748b" }}
            >
              Group Type
            </Typography>
            <Chip
              label={viewingItem?.groupType || "Unknown"}
              size="small"
              sx={{
                bgcolor:
                  viewingItem?.groupType === "Mixed" ? "#fff3cd" : "#e8f5e8",
                color:
                  viewingItem?.groupType === "Mixed" ? "#856404" : "#2e7d32",
                fontWeight: 500,
              }}
            />
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#64748b" }}
            >
              Status
            </Typography>
            <Chip
              label={viewingItem?.status || "Unknown"}
              size="small"
              color={viewingItem?.status === "Active" ? "success" : "default"}
            />
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#64748b" }}
            >
              Accounts Count
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#1e293b", fontWeight: 500 }}
            >
              {viewingItem?.accountsCount || 0}{" "}
              {(viewingItem?.accountsCount || 0) === 1 ? "account" : "accounts"}
            </Typography>
          </Box>
          {viewingItem?.country?.countryName &&
            viewingItem.country.countryName !== "N/A" && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "#64748b" }}
                >
                  Country
                </Typography>
                <Typography variant="body1" sx={{ color: "#1e293b" }}>
                  {viewingItem.country.countryName}
                  {viewingItem.country.countryCode &&
                    viewingItem.country.countryCode !== "N/A" && (
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: "#64748b", ml: 1 }}
                      >
                        ({viewingItem.country.countryCode})
                      </Typography>
                    )}
                </Typography>
              </Box>
            )}
        </Box>
      </Grid>

      {/* Details Section */}
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "#1e293b", mb: 2 }}
        >
          Details
        </Typography>

        {/* Description */}
        {viewingItem?.description && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#64748b", mb: 1 }}
            >
              Description
            </Typography>
            <Paper sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2 }}>
              <Typography
                variant="body1"
                sx={{ color: "#475569", lineHeight: 1.6 }}
              >
                {viewingItem.description}
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Notes - Always show with proper text wrapping */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "#64748b", mb: 1 }}
          >
            📝 Notes
          </Typography>
          <Paper
            sx={{
              p: 3,
              bgcolor: "#f8fafc",
              borderRadius: 2,
              border: "1px solid #e2e8f0",
              // ADDED: Proper overflow handling for group notes
              overflow: "hidden",
              wordWrap: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {viewingItem?.notes && viewingItem.notes.trim() !== "" ? (
              <Typography
                variant="body1"
                sx={{
                  color: "#475569",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                  // ADDED: Comprehensive text wrapping and overflow handling
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  hyphens: "auto",
                  maxWidth: "100%",
                  overflow: "hidden",
                  // ADDED: Better handling for very long strings
                  fontFamily:
                    viewingItem.notes.length > 200 ? "monospace" : "inherit",
                  fontSize:
                    viewingItem.notes.length > 500 ? "0.825rem" : "1rem",
                  // ADDED: Scroll for extremely long content
                  maxHeight: "300px",
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f5f9",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#cbd5e1",
                    borderRadius: "3px",
                    "&:hover": {
                      backgroundColor: "#94a3b8",
                    },
                  },
                }}
              >
                {viewingItem.notes}
              </Typography>
            ) : (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "#94a3b8", fontStyle: "italic", mb: 1 }}
                >
                  No notes available for this group.
                </Typography>
                <Typography variant="caption" sx={{ color: "#cbd5e1" }}>
                  You can add notes when editing this group.
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Grid>

      {/* Associated Account IDs */}
      {canEditSettings &&
        viewingItem?.accountIds &&
        viewingItem.accountIds.length > 0 && (
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#1e293b", mb: 2 }}
            >
              Associated Account IDs
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {viewingItem.accountIds.map((accountId, index) => (
                <Chip
                  key={index}
                  label={accountId}
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: "monospace" }}
                />
              ))}
            </Box>
          </Grid>
        )}

      {/* Timeline */}
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "#1e293b", mb: 2 }}
        >
          Timeline
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          {viewingItem?.createdAt && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#64748b" }}
              >
                Created At
              </Typography>
              <Typography variant="body1" sx={{ color: "#1e293b" }}>
                {new Date(viewingItem.createdAt).toLocaleString()}
              </Typography>
            </Box>
          )}
          {viewingItem?.updatedAt && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#64748b" }}
              >
                Last Updated
              </Typography>
              <Typography variant="body1" sx={{ color: "#1e293b" }}>
                {new Date(viewingItem.updatedAt).toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default ViewDetailsModal;
